import React, { useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import connect from '../../utils/connect';

import sportsApi from '../../utils/sportsApi';

import { LoadingDots } from '@app/ui';

import ErrorBoundary from '../../ErrorBoundary';
import CrashedComponent from '../../components/errors/CrashedComponent';

import BoundingBoxModal from '../BoundingBoxModal';

import ConfirmedPlacements from './confirmedPlacements/ConfirmedPlacements';
import ConfirmedButtons from './confirmedPlacements/Buttons';
import CustomContent from '../custom/CustomContent';
import CustomButtons from '../custom/Buttons';
import { Context } from '../contexts/OnboardingContext';
import { PlacementContext } from '../contexts/PlacementContext';

const PlacementsPanel = () => {
    const { t } = useTranslation('settings');
    const {
        rhId,
        isLoading,
        structureLocked,
        setPlacementModalOpen,
        currentSelectedPlacement,
        setSponsorsToAdd,
        sponsorsToRemove,
        setSponsorsToRemove,
        placements,
        sportId,
        hasStandardPlacements,
    } = useContext(Context);

    const {
        showPlacementUploader,
        setShowPlacementUploader,
        setPlacementTypeOptions,
        setPlacementOptions,
        setPlacementTypesLoading,
        files,
        setFiles,
        setRhPls,
        onCustomTab,
        setOnCustomTab,
        showModal,
        setShowModal,
        placementExampleId,
        setPlacementExampleId,
        addToSponsorList,
        showReloImages,
        reloImages,
        reloImageId,
        setReloImageId,
        setShowReloImages,
    } = useContext(PlacementContext);

    useEffect(() => {
        setOnCustomTab(false);
        setShowPlacementUploader(false);
    }, [rhId]);

    useEffect(() => {
        const last = sponsorsToRemove[sponsorsToRemove.length - 1];
        addToSponsorList(last);
    }, [sponsorsToRemove]);

    useEffect(() => {
        setPlacementModalOpen(showPlacementUploader);
    }, [showPlacementUploader]);

    useEffect(() => {
        if (!rhId) return;
        let cancelled = false;

        (async () => {
            setPlacementTypesLoading(true);
            try {
                let endPoint = `/placement-types`;
                const rhPlsEndpoint = `/rightsholders/${rhId}/placements`;
                if (Number.isInteger(sportId))
                    endPoint += `?sportId=${sportId}`;

                const [types, newRhPls] = await Promise.all([
                    sportsApi.url(endPoint).get().json(),
                    sportsApi.url(rhPlsEndpoint).get().json(),
                ]);

                if (cancelled) return;

                const options = types
                    .map(s => ({
                        value: s.id,
                        name: s.name,
                    }))
                    .sort((a, b) => {
                        return a.name.localeCompare(b.name);
                    });

                setRhPls(newRhPls);
                setPlacementTypeOptions(options);
            } catch (e) {
                // eslint-disable-next-line no-console
                console.log(e);
            } finally {
                if (!cancelled) setPlacementTypesLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [rhId]);

    useEffect(() => {
        if (!rhId) return;

        const options = placements
            .reduce((acc, p) => {
                if (acc.findIndex(el => el.name === p.name) > -1) return acc;
                const ind = acc.length;
                acc.push({
                    value: ind,
                    name: p.name,
                });
                return acc;
            }, [])
            .sort((a, b) => {
                return a.name.localeCompare(b.name);
            });

        setPlacementOptions(options);
    }, [rhId, placements]);

    const currentPlacementId = currentSelectedPlacement
        ? currentSelectedPlacement.id
        : null;

    useEffect(() => {
        setSponsorsToAdd([]);
        setSponsorsToRemove([]);
    }, [currentPlacementId]);

    return (
        <div className="onboarding-placements gds-flex-grid__item gds-flex-grid__item--desktop-2 gds-flex-grid__item--tablet-1 gds-flex-grid__item--mobile-1 gds-flex__item--basis-pct-lg-60">
            <div className="gds-card gds-flex-grid__item--full-height -m-b-0">
                <div className="gds-card__block -p-t-3 -p-h-3 -p-b-0">
                    <div className="gds-flex gds-flex--align-center gds-flex--justify-between">
                        <div className="gds-flex__item gds-flex__item--grow-0">
                            <p className="gds-text--header-sm gds-text--regular">
                                {t('clientConfiguration.onboarding.placements')}
                            </p>
                        </div>

                        {!onCustomTab && <ConfirmedButtons />}

                        {onCustomTab && <CustomButtons type="placement" />}
                    </div>
                    {isLoading && (
                        <div
                            className="gds-flex gds-flex--justify-center gds-flex--align-center -m-t-3"
                            style={{ minHeight: '300px', width: '100%' }}
                        >
                            <LoadingDots size="lg" />
                        </div>
                    )}
                    {!isLoading && (
                        <>
                            {!hasStandardPlacements && (
                                <nav
                                    className="-m-b-2"
                                    data-gds-nav-controls=""
                                >
                                    <ul
                                        className="gds-nav-tabs gds-nav-tabs--secondary gds-nav-tabs--sm gds-nav-tabs--underline"
                                        data-gds-nav="5"
                                    >
                                        <li
                                            className={`gds-nav-tabs__list-item gds-nav-tabs__list-item--underline ${
                                                !onCustomTab
                                                    ? `gds-nav-tabs__list-item--active`
                                                    : ''
                                            }`}
                                            data-gds-nav-item=""
                                        >
                                            <a
                                                className="gds-nav-tabs__link gds-nav-tabs__link--underline"
                                                data-gds-nav-button="1"
                                                data-gds-nav-type="tab"
                                                onClick={() => {
                                                    setOnCustomTab(false);
                                                }}
                                            >
                                                {t(
                                                    'clientConfiguration.onboarding.confirmedPlacements',
                                                )}
                                            </a>
                                        </li>

                                        <li
                                            className={`gds-nav-tabs__list-item gds-nav-tabs__list-item--underline ${
                                                onCustomTab
                                                    ? `gds-nav-tabs__list-item--active`
                                                    : ''
                                            }`}
                                            data-gds-nav-item=""
                                        >
                                            <a
                                                className="gds-nav-tabs__link gds-nav-tabs__link--underline"
                                                data-gds-nav-button="2"
                                                data-gds-nav-type="tab"
                                                onClick={() => {
                                                    setOnCustomTab(true);
                                                }}
                                            >
                                                {t(
                                                    'clientConfiguration.onboarding.customPlacements',
                                                )}
                                            </a>
                                        </li>
                                    </ul>
                                </nav>
                            )}

                            {(hasStandardPlacements || !onCustomTab) && (
                                <ConfirmedPlacements />
                            )}

                            {!hasStandardPlacements && onCustomTab && (
                                <CustomContent type="placement" />
                            )}
                        </>
                    )}
                </div>
            </div>

            <ErrorBoundary FallbackComponent={CrashedComponent}>
                <BoundingBoxModal
                    placement={currentSelectedPlacement}
                    placementExampleId={
                        showReloImages ? reloImageId : placementExampleId
                    }
                    showModal={showModal}
                    close={() => {
                        setShowModal(false);
                        if (showReloImages) {
                            setShowReloImages(false);
                        }
                    }}
                    setPlacementExampleId={
                        showReloImages ? setReloImageId : setPlacementExampleId
                    }
                    rhId={rhId}
                    files={showReloImages ? reloImages : files}
                    setFiles={setFiles}
                    canEdit={!structureLocked}
                    showReloImages={showReloImages}
                />
            </ErrorBoundary>
        </div>
    );
};

export default connect(['user', 'client'])(PlacementsPanel);
